import React, { useEffect, useState } from "react";
import TableOne from "../table/TableOne";
import { getRequest, putRequest } from "../api/api";
import { Avatar, Tag, Select, Rate, Button, Modal, Space, message , Tooltip, Badge} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import "./../style/common.css"
import { MdFeedback } from "react-icons/md";
import dayjs from "dayjs";
import Swal from "sweetalert2";


const { Option } = Select;

const Feedback = () => {
  const [feedbackData, setFeedbackData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("All");

  // modal state
  const [viewVisible, setViewVisible] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [statusView, setStatusView] = useState("active"); // "active" | "archived"

  // bulk select
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fetchFeedbacks = async (view = statusView) => {
  setLoading(true);
  try {
    // server-side filter via query param
    const qs = view === "archived" ? "?isArchived=true" : "?isArchived=false";
    const res = await getRequest(`feedback${qs}`);

    // res shape: { archived_mode: boolean, count: number, data: [...] }
    const rows = Array.isArray(res?.data) ? res.data : [];

    const formattedData = rows.map((item) => ({
      key: item.id,
      id: item.id,
      name: `${item.user?.first_name || ""} ${item.user?.last_name || ""}`.trim(),
      email: item.user?.email || "-",
      profile: item.user?.profile_image || "",
      message: item.message,
      response: item.response ?? "-",
      rating: Number(item.rating || 0),
      feedbackType: item.chapter_id ? "Chapter Feedback" : "App Feedback",
      chapterTitle: item.chapter?.title || "-",
      messageAt: item.message_at,
      responseAt: item.response_at || "-",
      is_read: Number(item.is_read ?? 0), // 0/1/2
      raw: item,
    }));

    formattedData.sort((a, b) => new Date(b.messageAt) - new Date(a.messageAt));

    setFeedbackData(formattedData);
    // still apply your local type/status filters for safety
    applyFilters(formattedData, filterType, view);
  } catch (error) {
    console.error("Failed to fetch feedbacks:", error);
    message.error("Failed to load feedbacks");
  } finally {
    setLoading(false);
  }
};



useEffect(() => {
  fetchFeedbacks(statusView); // default "active"
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const handleFilterChange = (value) => {
  setFilterType(value);
  setSelectedRowKeys([]);
  applyFilters(feedbackData, value, statusView);
};

const handleStatusViewChange = (value) => {
  setStatusView(value);
  setSelectedRowKeys([]);
  // fetch with the chosen view; type filter is handled in applyFilters
  fetchFeedbacks(value);
};




  const truncate = (txt, n = 60) => (!txt ? "-" : txt.length > n ? txt.slice(0, n) + "…" : txt);

  // Single mark-as-read when opening modal
const openDetails = async (record) => {
  setActiveItem(record);
  setViewVisible(true);

  if (record.is_read === 0) {
    try {
      await putRequest(`feedback/read-status/${record.id}`, { is_read: 1 });
      setFeedbackData(prev => prev.map(r => r.id === record.id ? { ...r, is_read: 1 } : r));
      setFilteredData(prev => prev.map(r => r.id === record.id ? { ...r, is_read: 1 } : r));
    } catch (e) {
      console.error(e);
      message.error("Failed to mark as read");
    }
  }
};


  // Bulk mark-as-read
  const handleBulkMarkRead = async () => {
    if (!selectedRowKeys.length) return;
    try {
      await putRequest("feedback/read-status", {
        ids: selectedRowKeys,
        is_read: 1,
      });
      setFeedbackData((prev) =>
        prev.map((r) => (selectedRowKeys.includes(r.id) ? { ...r, is_read: 1 } : r))
      );
      setFilteredData((prev) =>
        prev.map((r) => (selectedRowKeys.includes(r.id) ? { ...r, is_read: 1 } : r))
      );
      setSelectedRowKeys([]);
      message.success("Selected feedback marked as read");
    } catch (e) {
      console.error(e);
      message.error("Failed to update selected feedback");
    }
  };

  // at top of component
const [vw, setVw] = useState(() => window.innerWidth);
useEffect(() => {
  const onResize = () => setVw(window.innerWidth);
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);


const handleArchive = async (record) => {
  try {
    await putRequest(`feedback/read-status/${record.id}`, { is_read: 2 });

    // update local state
    setFeedbackData(prev => prev.map(r =>
      r.id === record.id ? { ...r, is_read: 2 } : r
    ));
    setFilteredData(prev => prev.map(r =>
      r.id === record.id ? { ...r, is_read: 2 } : r
    ));

    // also clear from selection if it was selected
    setSelectedRowKeys(keys => keys.filter(k => k !== record.id));
  fetchFeedbacks();
    message.success("Feedback archived");
  } catch (e) {
    console.error(e);
    message.error("Failed to archive");
  }
};
const applyFilters = (all = feedbackData, type = filterType, view = statusView) => {
  let rows = [...all];
  if (type !== "All") rows = rows.filter(r => r.feedbackType === type);

  if (view === "active") rows = rows.filter(r => r.is_read !== 2);
  else if (view === "archived") rows = rows.filter(r => r.is_read === 2);

  setFilteredData(rows);

};
const confirmArchive = (record) => {
  if (record.is_read === 2) return; // already archived, do nothing

  Modal.confirm({
    title: "Archive Feedback",
    content: "Are you sure you want to move this feedback to archive?",
    okText: "Yes, Archive",
    cancelText: "Cancel",
    okButtonProps: { danger: true },
    centered: true,
    onOk: async () => {
      await handleArchive(record);
    },
  });
};



// width only when 1024px–1600px
const userColWidth = vw >= 1024 && vw <= 1600 ? 280 : undefined; // pick any px you like

const columns = [
  {
    title: "No.",
    key: "index",
    render: (_, __, index) => index + 1,
    width: 60,
  },
  {
    title: "User",
    dataIndex: "name",
    key: "name",
    render: (text, record) => (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Avatar src={record.profile} />
        <div>
          <div style={{ fontWeight: record.is_read ? 500 : 700 }}>{text}</div>
          <div style={{ fontSize: 12, color: "#888" }}>{record.email}</div>
        </div>
      </div>
    ),
   width: userColWidth,
  },
  {
    title: "Type",
    dataIndex: "feedbackType",
    key: "feedbackType",
    render: (text) =>
      text === "Chapter Feedback" ? (
        <Tag color="blue">Chapter</Tag>
      ) : (
        <Tag color="green">App</Tag>
      ),
    width: 140,
  },
  {
    title: "Chapter Title",
    dataIndex: "chapterTitle",
    key: "chapterTitle",
    ellipsis: true,
  },
  // {
  //   title: "Rating",
  //   dataIndex: "rating",
  //   key: "rating",
  //   render: (rating) => <Rate disabled value={Number(rating)} allowHalf />,
  //   width: 200,
  // },
  {
    title: "Message",
    dataIndex: "message",
    key: "message",
    render: (text) => <span>{truncate(text)}</span>, // <-- only truncate here
    width: userColWidth,
  },
{
  title: "Read Status",
  dataIndex: "is_read",
  key: "is_read",
  width: 160,
  render: (val) => {
    let color = "#ef4444", label = "Unread";
    if (val === 1) { color = "#10b981"; label = "Read"; }
    if (val === 2) { color = "#6b7280"; label = "Archived"; } // gray
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, color }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color, display: "inline-block" }} />
        {label}
      </span>
    );
  },
}
,
{
  title: "Message At",
  dataIndex: "messageAt",
  key: "messageAt",
  width: 180,
  render: (val) => {
    if (!val) return "-";

    // Parse directly (without forcing utc conversion)
    const localTime = dayjs(val).format("DD MMM YYYY, hh:mm A");

    return (
      <Tooltip title={dayjs(val).format("YYYY-MM-DD HH:mm:ss")}>
        {localTime}
      </Tooltip>
    );
  },
  sorter: (a, b) => dayjs(a.messageAt).valueOf() - dayjs(b.messageAt).valueOf(),
  defaultSortOrder: "descend",
},


{
  title: "Details",
  key: "details",
  fixed: "right",
  width: 220,
  render: (_, record) => (
    <Space>
      <Tooltip title="View full feedback">
        <Badge dot={record.is_read === 0} color="#fa541c">
          <Button
            size="small"
            type={record.is_read === 1 ? "default" : "primary"}
            icon={<FileTextOutlined />}
            onClick={() => openDetails(record)}
          >
            Read more
          </Button>
        </Badge>
      </Tooltip>

      <Tooltip title={record.is_read === 2 ? "Already archived" : "Move to Archive"}>
        <Button
          size="small"
          danger
          onClick={() => confirmArchive(record)}
          disabled={record.is_read === 2}
        >
          Archive
        </Button>
      </Tooltip>
    </Space>
  ),
}


];




  return (
    <div>
      <div
        className="header-button"
        style={{
          padding: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
    <h2
  style={{
    margin: 0,
    color: "#5a355a",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    lineHeight: 1,
  }}
>
  <MdFeedback />
  <span>All Feedbacks</span>
  <span
    style={{
      color: "#979797ff",
      fontSize: "14px",
      fontWeight: 500,
      alignSelf: "flex-end", // bottom-align just the total
    }}
  >
    (Total: {feedbackData?.length ?? 0})
  </span>
</h2>

        <Space wrap>
          <Select value={filterType} onChange={handleFilterChange} style={{ width: 200 }}>
            <Option value="All">All Feedback</Option>
            <Option value="Chapter Feedback">Chapter Feedback</Option>
            <Option value="App Feedback">App Feedback</Option>
          </Select>

            {/* Status filter (Active vs Archived) */}
  <Select value={statusView} onChange={handleStatusViewChange} style={{ width: 180 }}>
    <Option value="active">Unarchived</Option>
    <Option value="archived">Archived</Option>
  </Select>

          <Button
            type="primary"
            onClick={handleBulkMarkRead}
            disabled={!selectedRowKeys.length}
            className="mark-read-btn"
          >
            Mark as Read ({selectedRowKeys.length})
          </Button>
        </Space>
      </div>

<TableOne
  columns={columns}
  data={filteredData}
  loading={loading}
  rowSelection={{
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  }}
  rowKeyField="id"   // optional (defaults to 'id')
  
/>


      <Modal
        open={viewVisible}
        onCancel={() => setViewVisible(false)}
        footer={null}
        width={640}
        destroyOnClose
        title="Feedback Details"
      >
        {activeItem && (
          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar size={48} src={activeItem.profile} />
              <div>
                <div style={{ fontWeight: 600 }}>{activeItem.name}</div>
                <div style={{ color: "#888", fontSize: 12 }}>{activeItem.email}</div>
              </div>
              <div style={{ marginLeft: "auto" }}>
                {activeItem.is_read ? <Tag color="success">Read</Tag> : <Tag color="red">Unread</Tag>}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Tag>{activeItem.feedbackType}</Tag>
              {activeItem.chapterTitle !== "-" && <Tag color="blue">{activeItem.chapterTitle}</Tag>}
              <Rate disabled value={activeItem.rating} allowHalf />
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Message</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{activeItem.message || "NA"}</div>
            </div>

            {/* <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Response</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{activeItem.response || "-"}</div>
            </div> */}

            <div style={{ display: "grid", gap: 4, color: "#666" }}>
              <div>Message At: {activeItem.messageAt}</div>
              {activeItem.responseAt && activeItem.responseAt !== "-" && (
                <div>Response At: {activeItem.responseAt}</div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Feedback;
